import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { Tooltip } from '@mui/material';
import format from 'string-template';

import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import LoadingPage from '../../../../../../components/LoadingPage';
import { DateFormat } from '../../../../../../utils/date';
import {
  cancelChallengeContract,
  cancelSubscription,
  listInvoices,
  updateSubscription,
  retryInvoicePayment,
  pauseStripeSubscription,
  unpauseStripeSubscription,
} from '../../../../../../pages/Onboarding/Subscription/stripe/SubscriptionRemoteRequestHandler';
import FirebaseContext from '../../../../../../context/FirebaseContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import User from '../../../../../../Model/User';
import StripeCustomerDocument from '../../../../../../Model/StripeCustomerDocument';
import { StripeSubscriptionState } from '../../../../../../pages/Onboarding/Subscription/SubscriptionMetadata';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import useLogger from '../../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../../utils/log';
import LoadingOverlay from '../../../../LoadingOverlay';
import ConfirmDialog from '../../../../ConfirmDialog';
import ContractChooserModal, { useContractChoice } from '../../../../ContractChooserModal';
import useToast from '../../../../../hooks/useToast';
import { ClientStatus, isOneTimePayer } from '../../../../../utils/statusFilter';
import { getContractObj } from '../../../../../utils/userContract';
import {
  AmendmentBlocker,
  amendmentBlockerFor,
  findTargetForInvoice,
  canAmendContract,
  canCancelSubscription,
  canPauseSubscription,
  canReonboardClient,
  AMENDABLE_STATUSES,
  canUnpauseSubscription,
  canUpdateStartDate,
  isChallengeTarget,
  isLiveTarget,
  isOneTimeChallengeTarget,
  isSubscriptionInvoice,
  oneTargetPerProduct,
  productLabel,
  stripeSubscriptionOf,
} from '../../../../../utils/billingTarget';

import EmptyView from '../../EmptyView';

import VoidInvoiceModal from '../VoidInvoiceModal';
import RefundInvoiceModal from '../RefundInvoiceModal/RefundInvoiceModal';
import SubscriptionCancelDialog from '../SubscriptionCancelModal';
import StartDateUpdateModal from '../StartDateUpdateModal';
import SubscriptionPauseModal from '../SubscriptionPauseModal';
import OneTimePaymentPanel from './OneTimePaymentPanel';
import InvoiceTable from './InvoiceTable';
import {
  Container,
  HeaderContainer,
  DataContainer,
  Title,
  StyledButton,
  StyledCancelSubscriptionIcon,
  EditSubscriptionIcon,
  StyledPauseSubscriptionIcon,
  StyledUnpauseSubscriptionIcon,
  StyledReonboardIcon,
} from './styles';
import texts from './texts.json';
import { DisputeStatus } from './constants';

const CANCELLATION_SOURCE_DASHBOARD = 'DASHBOARD';

const BillingAction = {
  CANCEL: 'CANCEL',
  PAUSE: 'PAUSE',
  UNPAUSE: 'UNPAUSE',
  START_DATE: 'START_DATE',
  AMEND: 'AMEND',
};

const HEADER_ACTIONS = [
  { action: BillingAction.START_DATE, Icon: EditSubscriptionIcon, label: texts.updateStartDate },
  { action: BillingAction.CANCEL, Icon: StyledCancelSubscriptionIcon, label: texts.cancelSubscription },
  { action: BillingAction.PAUSE, Icon: StyledPauseSubscriptionIcon, label: texts.pauseSubscription },
  { action: BillingAction.UNPAUSE, Icon: StyledUnpauseSubscriptionIcon, label: texts.unpauseSubscription },
];

const AMENDMENT_BLOCKER_TEXT = {
  [ClientStatus.ON_BREAK]: texts.primaryContractDisabledOnBreak,
  [ClientStatus.FUTURE_START]: texts.primaryContractDisabledFutureStart,
  [ClientStatus.INACTIVE]: texts.primaryContractDisabledNotActiveClient,
  [AmendmentBlocker.NO_SUBSCRIPTION]: texts.primaryContractDisabledNoSubscription,
};

// Billing calls can fail as returned API payloads, thrown Error instances, or plain strings.
// Normalize those shapes before showing the message in the UI or sending it to Sentry.
const getBillingErrorMessage = (errorResponse) => {
  if (errorResponse?.error?.message) {
    return errorResponse.error.message;
  }
  if (typeof errorResponse?.error === 'string') {
    return errorResponse.error;
  }
  if (errorResponse?.message) {
    return errorResponse.message;
  }
  if (typeof errorResponse === 'string') {
    return errorResponse;
  }
  return texts.loadBillingError;
};

const BillingSummary = ({
  userDoc,
  billingTargets,
  isBlockedByFutureContract,
  scheduledAmendmentContractIds,
  onOpenContractModal,
}) => {
  const [invoiceList, setInvoiceList] = useState([]);
  const [error, setError] = useState();
  const [isReady, setIsReady] = useState(false);
  const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);
  const [showConfirmPauseDialog, setShowConfirmPauseDialog] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [isUpdatingInProgress, setIsUpdatingInProgress] = useState(false);
  const [showRetryPaymentDialog, setShowRetryPaymentDialog] = useState(false);
  const [showVoidInvoiceDialog, setShowVoidInvoiceDialog] = useState(false);
  const [showRefundInvoiceDialog, setShowRefundInvoiceDialog] = useState(false);
  const [refundedInvoiceId, setRefundedInvoiceId] = useState(null);
  const [isRetryingInProgress, setIsRetryingInProgress] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState();
  const [actionTarget, setActionTarget] = useState(null);
  // Pause and unpause show their result before the server write lands.
  const [breakEndDateByContract, setBreakEndDateByContract] = useState({});
  const isComponentMountedRef = useComponentMounted();
  const { firebase: { remote } } = useContext(FirebaseContext);
  /*
    The same account the contracts beside this list are narrowed to. Without it the two disagree:
    the other coach's invoices fail to match a contract and render as unknown-product rows with
    void, retry and refund still live on them.
  */
  const { coachDoc, externalCoachDoc } = useContext(ExternalCoachContext);
  const pageStripeAccountId = externalCoachDoc?.stripeAccountId;
  const pageCoachName = coachDoc?.name;
  const { showToast } = useToast();
  const { isCoach, isAdmin, isOnCall } = useSessionStore();
  const { logCoachingActivity } = useLogger();
  const {
    pendingCandidates,
    pendingTitle,
    request: requestContractChoice,
    confirm: confirmContractChoice,
    dismiss: dismissContractChoice,
  } = useContractChoice();

  const coachStripeAccountId = billingTargets
    .find(({ subscriptionDoc }) => !!subscriptionDoc)?.subscriptionDoc?.stripeAccountId;

  const targets = useMemo(() => billingTargets.map((target) => {
    const optimisticBreakEnd = breakEndDateByContract[target.contractId];
    if (!optimisticBreakEnd) {
      return target;
    }
    return { ...target, clientView: { ...target.clientView, breakEndDate: optimisticBreakEnd } };
  }), [billingTargets, breakEndDateByContract]);

  const hasAnySubscription = targets.some(({ subscriptionDoc }) => !!subscriptionDoc);

  useEffect(() => {
    const init = async () => {
      try {
        /*
          A client with a subscription always has a Stripe customer; only look it up for the
          no-subscription case, where its absence means "skip the fetch" (comped clients) rather
          than an unexpected failure.
        */
        const hasStripeCustomer = hasAnySubscription
          ? true
          : !!(await StripeCustomerDocument.getStripeCustomerByUserId(userDoc.id, pageStripeAccountId))?.id;

        const invoices = hasStripeCustomer
          ? await listInvoices(userDoc.id, remote, undefined, {
            includeChargeDetails: true,
            stripeAccountId: pageStripeAccountId,
          })
          : [];

        if (!isComponentMountedRef.current) {
          return;
        }
        if (invoices && Array.isArray(invoices)) {
          setInvoiceList(invoices);
        } else if (invoices) {
          const errorMessage = getBillingErrorMessage(invoices);
          Sentry.captureException(new Error(errorMessage), {
            extra: {
              userId: userDoc.id,
              invoiceResponse: invoices,
            },
          });
          setError(errorMessage);
        }
      } catch (requestError) {
        Sentry.captureException(requestError, {
          extra: {
            userId: userDoc.id,
          },
        });
        if (isComponentMountedRef.current) {
          setError(getBillingErrorMessage(requestError));
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsReady(true);
        }
      }
    };
    if (!isReady) {
      init();
    }
  }, [
    userDoc,
    isReady,
    isComponentMountedRef,
    remote,
    hasAnySubscription,
    pageStripeAccountId,
  ]);

  const cancelClientSubscription = useCallback(async (cancellationReason, cancelType) => {
    setShowConfirmCancelDialog(false);
    setIsUpdatingInProgress(true);
    // A challenge with no Stripe subscription ends through its own action, keyed by the contract.
    const isChallenge = isOneTimeChallengeTarget(actionTarget);
    const subscriptionId = stripeSubscriptionOf(actionTarget)?.id;
    const cancellation = {
      remote,
      cancellationReason,
      cancelType,
      cancellationSource: CANCELLATION_SOURCE_DASHBOARD,
    };
    const { isSuccess } = isChallenge
      ? await cancelChallengeContract({ ...cancellation, userId: userDoc.id, contractId: actionTarget.contractId })
      : await cancelSubscription({ ...cancellation, subscriptionId, active: true });
    if (isSuccess) {
      showToast(isChallenge ? texts.cancelChallengeSuccess : format(texts.cancelSuccess, { subscriptionId }));

      logCoachingActivity(CoachingActivity.CANCELED_USER, {
        cancelType,
        cancellationReason,
        clientId: userDoc.id,
      });
    } else {
      showToast(
        isChallenge ? texts.cancelChallengeError : format(texts.cancelError, { subscriptionId }),
        { error: true },
      );
    }
    setIsUpdatingInProgress(false);
  }, [
    actionTarget,
    remote,
    showToast,
    userDoc,
    logCoachingActivity,
  ]);

  const updateClientSubscription = useCallback(async (updateParams, cancellationReason, cancelType) => {
    setShowConfirmCancelDialog(false);
    setIsUpdatingInProgress(true);
    setShowStartDateModal(false);

    const subscriptionId = stripeSubscriptionOf(actionTarget)?.id;
    const { updatedSubscription, error: apiError } = await updateSubscription({
      subscriptionId,
      updateParams,
      remote,
      cancellationReason,
      cancelType,
      cancellationSource: CANCELLATION_SOURCE_DASHBOARD,
    });
    if (updatedSubscription) {
      const successText = cancellationReason ? texts.futureCancelSuccess : texts.updateSuccess;

      /* We only execute this part if we are updating the start date, not when cancelling the subscription
        It updates the contract terms when changing start date */
      if (!cancelType && actionTarget?.contractDoc) {
        const contractObj = getContractObj(actionTarget.contractDoc);
        // Update the startDate
        contractObj.startDate = moment.unix(updateParams.trial_end);
        // Update the doc
        await actionTarget.contractDoc.updateContractTermsFromContractData(contractObj);
      }

      showToast(format(successText, { subscriptionId }));
    } else if (apiError) {
      showToast(format(texts.updateError, {
        subscriptionId,
        error: apiError.message || '',
      }), { error: true });
    }
    setIsUpdatingInProgress(false);
  }, [
    actionTarget,
    remote,
    showToast,
  ]);

  const pauseClientSubscription = useCallback(async (pausingReason, breakEndDate, pausedPeriodInMonths) => {
    setShowConfirmPauseDialog(false);
    setIsUpdatingInProgress(true);
    const subscriptionId = stripeSubscriptionOf(actionTarget)?.id;
    const { contractId } = actionTarget || {};
    const { isSuccess } = await pauseStripeSubscription({
      subscriptionId,
      contractId,
      remote,
      pausingReason,
      breakEndDate,
    });
    if (isSuccess) {
      setBreakEndDateByContract((previous) => ({ ...previous, [contractId]: breakEndDate }));
      showToast(format(texts.pauseSuccess, { subscriptionId }));
      logCoachingActivity(CoachingActivity.PAUSED_USER, {
        pausingReason,
        pausedPeriodInMonths,
        clientId: userDoc.id,
      });
    } else {
      showToast(format(texts.pauseError, { subscriptionId }), { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsUpdatingInProgress(false);
    }
  }, [
    actionTarget,
    remote,
    showToast,
    isComponentMountedRef,
    logCoachingActivity,
    userDoc,
  ]);

  const unpauseClientSubscription = useCallback(async (target) => {
    setIsUpdatingInProgress(true);
    const subscriptionId = stripeSubscriptionOf(target)?.id;
    const { contractId } = target;
    const { isSuccess } = await unpauseStripeSubscription({
      subscriptionId,
      contractId,
      remote,
    });
    if (isSuccess) {
      setBreakEndDateByContract((previous) => ({ ...previous, [contractId]: new Date() }));
      showToast(format(texts.unpauseSuccess, { subscriptionId }));
    } else {
      showToast(format(texts.unpauseError, { subscriptionId }), { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsUpdatingInProgress(false);
    }
  }, [
    remote,
    isComponentMountedRef,
    showToast,
  ]);

  const runAction = useCallback((action, target) => {
    setActionTarget(target);
    switch (action) {
      case BillingAction.CANCEL:
        setShowConfirmCancelDialog(true);
        break;
      case BillingAction.PAUSE:
        setShowConfirmPauseDialog(true);
        break;
      case BillingAction.START_DATE:
        setShowStartDateModal(true);
        break;
      case BillingAction.UNPAUSE:
        unpauseClientSubscription(target);
        break;
      case BillingAction.AMEND:
        onOpenContractModal(true, target.contractId, target.productId);
        break;
      default:
        break;
    }
  }, [
    unpauseClientSubscription,
    onOpenContractModal,
  ]);

  const handleRetryButtonClick = useCallback((invoice) => {
    setCurrentInvoice(invoice);
    setShowRetryPaymentDialog(true);
  }, []);

  const handleVoidInvoiceButtonClick = useCallback((invoice) => {
    setCurrentInvoice(invoice);
    setShowVoidInvoiceDialog(true);
  }, []);

  const handleRefundButtonClick = useCallback((invoice) => {
    setCurrentInvoice(invoice);
    setShowRefundInvoiceDialog(true);
  }, []);

  /*
    Invoices span every product, so the account and the contract both come from the invoice's
    own target. The server holds a coach to the contract named here, so they travel together.
  */
  const invoiceScopeFor = useCallback((invoice) => {
    const target = findTargetForInvoice(targets, invoice);
    return {
      stripeAccountId: target?.subscriptionDoc?.stripeAccountId || coachStripeAccountId,
      contractId: target?.contractId,
      productName: target ? productLabel(target) : '',
    };
  }, [targets, coachStripeAccountId]);

  const retryCurrentInvoicePayment = useCallback(async () => {
    setShowRetryPaymentDialog(false);
    setIsRetryingInProgress(true);
    const { stripeAccountId: retryAccountId, contractId: retryContractId } = invoiceScopeFor(currentInvoice);
    const { invoice, error: stripeError } = await retryInvoicePayment({
      invoiceId: currentInvoice?.id,
      stripeAccountId: retryAccountId,
      contractId: retryContractId,
      remote,
    });

    const invoiceDate = (currentInvoice
      && moment.unix(currentInvoice.created).format(DateFormat.MONTH_NAME_DATE_FORMAT)) || '';
    if (stripeError) {
      showToast(format(texts.retryError, {
        invoiceDate,
        error: stripeError.message || '',
      }), { error: true });
    } else if (invoice) {
      showToast(format(texts.retrySuccess, {
        invoiceDate,
      }));
      setIsReady(false);
    }
    setIsRetryingInProgress(false);
  }, [
    invoiceScopeFor,
    remote,
    showToast,
    currentInvoice,
  ]);

  const reloadInvoices = useCallback(async () => {
    const invoices = await listInvoices(userDoc.id, remote, undefined, {
      includeChargeDetails: true,
      stripeAccountId: pageStripeAccountId,
    });
    if (isComponentMountedRef.current) {
      setInvoiceList(invoices);
    }
  }, [userDoc, remote, isComponentMountedRef, pageStripeAccountId]);

  const onInvoiceRefunded = useCallback(async (invoiceId, fullyRefunded = true) => {
    setShowRefundInvoiceDialog(false);
    // Only drop refund eligibility when the invoice is fully refunded; partial refunds
    // keep the button so the remaining balance can still be refunded.
    if (fullyRefunded) {
      setRefundedInvoiceId(invoiceId);
    }
    await reloadInvoices();
  }, [reloadInvoices]);

  const onInvoiceVoided = useCallback(async () => {
    setShowVoidInvoiceDialog(false);
    await reloadInvoices();
  }, [reloadInvoices]);

  const canManageBilling = !error && (isCoach || isAdmin);

  const candidatesByAction = useMemo(() => {
    const pick = (isEligible, isBlocked) => (isBlocked ? [] : oneTargetPerProduct(targets.filter(isEligible)));
    return {
      [BillingAction.START_DATE]: pick(canUpdateStartDate, !canManageBilling),
      /*
        A challenge is a one-time purchase: only support ends one early, and the server refuses anyone
        else, whether or not a subscription bills it.
      */
      [BillingAction.CANCEL]: pick(
        (target) => canCancelSubscription(target) && (!isChallengeTarget(target) || isOnCall),
        !canManageBilling,
      ),
      [BillingAction.PAUSE]: pick(canPauseSubscription, !!error),
      [BillingAction.UNPAUSE]: pick(canUnpauseSubscription, false),
      [BillingAction.AMEND]: pick(
        (target) => canAmendContract(target) && !isBlockedByFutureContract(target.contractId, target.productId),
        !!error,
      ),
    };
  }, [targets, canManageBilling, error, isBlockedByFutureContract, isOnCall]);

  /*
    Counted per product, matching the rows `listedTargets` renders, so the coach only ever sees a
    product named on a button when they can also see more than one row to confuse it with.
  */
  const liveProductCount = useMemo(() => oneTargetPerProduct(targets.filter(isLiveTarget)).length, [targets]);

  /*
    A single candidate skips the chooser, so the product it acts on would otherwise never be
    named — the ambiguous case is one actionable product among several live ones.
  */
  const labelForAction = useCallback((label, candidates) => (
    liveProductCount > 1 && candidates.length === 1
      ? format(texts.actionForProduct, { label, productName: productLabel(candidates[0]) })
      : label
  ), [liveProductCount]);

  const amendCandidates = candidatesByAction[BillingAction.AMEND];
  const canReonboard = !error && canReonboardClient(targets);

  // One action or the other, never both.
  const showAmendButton = amendCandidates.length > 0;
  /*
    A running product keeps the button even when nothing can be amended, so the coach is told
    why (on a break, not started yet, a future contract already pending) instead of the action
    silently disappearing.
  */
  const hasRunningProduct = targets.some(
    ({ clientView }) => AMENDABLE_STATUSES.includes(clientView.subscriptionStatus),
  );
  const showPrimaryContractButton = showAmendButton || canReonboard || (!error && hasRunningProduct);

  const amendmentDisabledReason = useMemo(() => {
    if (showAmendButton) {
      return '';
    }
    // Only when a product that could otherwise be amended is the one waiting on a signature.
    if (targets.some(
      (target) => canAmendContract(target) && isBlockedByFutureContract(target.contractId, target.productId),
    )) {
      return texts.primaryContractDisabledBlocking;
    }
    const liveTargets = targets.filter((target) => target.clientView.isActive);
    if (!liveTargets.length) {
      return texts.primaryContractDisabledNotActiveClient;
    }
    const blockers = new Set(liveTargets.map(amendmentBlockerFor));
    if (blockers.size === 1) {
      return AMENDMENT_BLOCKER_TEXT[[...blockers][0]] || texts.primaryContractDisabledNotActiveClient;
    }
    // Blocked for different reasons: naming one would be misleading.
    return texts.primaryContractDisabledNoneEligible;
  }, [showAmendButton, isBlockedByFutureContract, targets]);

  const primaryContractDisabledReason = canReonboard ? '' : amendmentDisabledReason;

  const handlePrimaryContractClick = useCallback(() => {
    if (showAmendButton) {
      requestContractChoice(amendCandidates, (target) => runAction(BillingAction.AMEND, target), texts.createNewTerms);
      return;
    }
    onOpenContractModal(false);
  }, [
    showAmendButton,
    amendCandidates,
    requestContractChoice,
    runAction,
    onOpenContractModal,
  ]);

  /**
   * The refundable invoice of each product — its most recent paid one. An amendment mints a new
   * contract for the same product, so keying on the contract would offer both its invoice and its
   * predecessor's. An invoice matching no billing target is its own group: nothing ties it to a
   * product, so it must not suppress another one.
   * Only computed when the current user is an internal admin (not a coach).
   */
  const refundableInvoiceIds = useMemo(() => {
    if (isCoach || !isAdmin) {
      return new Set();
    }
    // The list arrives newest first, so the first paid invoice per product is its most recent.
    const newestPaidPerProduct = new Map();
    invoiceList.forEach((invoice) => {
      if (invoice.status !== 'paid') {
        return;
      }
      const key = findTargetForInvoice(targets, invoice)?.productId || invoice.id;
      if (!newestPaidPerProduct.has(key)) {
        newestPaidPerProduct.set(key, invoice);
      }
    });

    const isRefundable = ({ id, charge }) => {
      const hasActiveDispute = charge?.disputed && charge?.disputeStatus !== DisputeStatus.WON;
      // Fully refunded once Stripe flags the charge or no refundable balance remains.
      // Partially-refunded invoices stay eligible so the remaining balance can still be refunded.
      const isFullyRefunded = charge
        ? (charge.refunded || (charge.amount != null && charge.amount - (charge.amountRefunded || 0) <= 0))
        : false;
      return !isFullyRefunded && !hasActiveDispute && id !== refundedInvoiceId;
    };

    return new Set([...newestPaidPerProduct.values()].filter(isRefundable).map(({ id }) => id));
  }, [isCoach, isAdmin, invoiceList, refundedInvoiceId, targets]);

  const invoiceProductLabel = useCallback((invoice) => {
    const target = findTargetForInvoice(targets, invoice);
    if (target) {
      return productLabel(target);
    }
    return isSubscriptionInvoice(invoice) ? texts.unknownProductLabel : texts.oneTimePaymentTitle;
  }, [targets]);

  const renderInvoiceAction = useCallback((invoice) => {
    const target = findTargetForInvoice(targets, invoice);
    // Gated on this invoice's own subscription: another product's status would show the
    // action on a dead subscription, or hide it on a live one.
    const invoiceSubscriptionStatus = stripeSubscriptionOf(target)?.status;
    /*
      Retry and void name the contract they act on, and the server refuses a request without one,
      so an invoice this page cannot place on a contract gets no action rather than a button that
      always fails.
    */
    const canActOnInvoice = invoice.status === 'open'
      && invoiceSubscriptionStatus !== StripeSubscriptionState.CANCELED
      && !!invoiceScopeFor(invoice).contractId;
    if (canActOnInvoice) {
      return (
        <>
          <StyledButton onClick={() => handleRetryButtonClick(invoice)}>
            {texts.retryPayment}
          </StyledButton>
          <StyledButton onClick={() => handleVoidInvoiceButtonClick(invoice)}>
            {texts.voidInvoice}
          </StyledButton>
        </>
      );
    }
    if (refundableInvoiceIds.has(invoice.id)) {
      return (
        <StyledButton onClick={() => handleRefundButtonClick(invoice)}>
          {texts.refundInvoice}
        </StyledButton>
      );
    }
    return texts.emptyText;
  }, [
    targets,
    invoiceScopeFor,
    refundableInvoiceIds,
    handleRetryButtonClick,
    handleVoidInvoiceButtonClick,
    handleRefundButtonClick,
  ]);

  if (!isReady) {
    return <LoadingPage />;
  }

  const actionSubscription = stripeSubscriptionOf(actionTarget);
  const isOneTimeChallenge = isOneTimeChallengeTarget(actionTarget);
  const currentInvoiceScope = currentInvoice ? invoiceScopeFor(currentInvoice) : {};
  const currentInvoiceAccountId = currentInvoiceScope.stripeAccountId;

  return (
    <Container>
      <HeaderContainer>
        {HEADER_ACTIONS
          .filter(({ action }) => candidatesByAction[action].length > 0)
          .map(({ action, Icon, label }) => (
            <StyledButton
              key={action}
              startIcon={<Icon />}
              onClick={() => requestContractChoice(
                candidatesByAction[action],
                (target) => runAction(action, target),
                label,
              )}
            >
              {labelForAction(label, candidatesByAction[action])}
            </StyledButton>
          ))}
        {showPrimaryContractButton && (
          <Tooltip
            title={primaryContractDisabledReason}
            disableHoverListener={!primaryContractDisabledReason}
          >
            <span>
              <StyledButton
                startIcon={<StyledReonboardIcon />}
                disabled={!!primaryContractDisabledReason}
                onClick={handlePrimaryContractClick}
              >
                {canReonboard
                  ? texts.reonboardClient
                  : labelForAction(texts.createNewTerms, amendCandidates)}
              </StyledButton>
            </span>
          </Tooltip>
        )}
      </HeaderContainer>
      {hasAnySubscription ? (
        <DataContainer>
          <Title>{texts.invoices}</Title>
          {invoiceList.length === 0 && <EmptyView text={error || texts.noInvoices} />}
          {invoiceList.length > 0 && (
            <InvoiceTable
              invoiceList={invoiceList}
              renderAction={renderInvoiceAction}
              productLabelFor={invoiceProductLabel}
            />
          )}
        </DataContainer>
      ) : (
        <>
          {isOneTimePayer(null, invoiceList)
            ? <OneTimePaymentPanel invoiceList={invoiceList} />
            : <EmptyView text={error || texts.noSubscription} />}
        </>
      )}
      <LoadingOverlay isLoading={isUpdatingInProgress || isRetryingInProgress} />
      {!!pendingCandidates && (
        <ContractChooserModal
          title={pendingTitle || texts.chooser.title}
          description={texts.chooser.description}
          candidates={pendingCandidates}
          onConfirm={confirmContractChoice}
          onCancel={dismissContractChoice}
        />
      )}
      {(!!actionSubscription || isOneTimeChallenge) && (
        <SubscriptionCancelDialog
          isChallenge={isOneTimeChallenge}
          isOpen={showConfirmCancelDialog}
          onCancel={() => setShowConfirmCancelDialog(false)}
          dialogTexts={{
            title: texts.confirmCancel.title,
            content: format(
              isOneTimeChallenge ? texts.confirmCancelChallenge.content : texts.confirmCancel.content,
              { userName: userDoc.name },
            ),
          }}
          subscription={actionSubscription}
          updateClientSubscription={updateClientSubscription}
          cancelClientSubscription={cancelClientSubscription}
          userContractDoc={actionTarget.contractDoc}
          userDoc={actionTarget.clientView}
          hasAcceptedAmendment={scheduledAmendmentContractIds.includes(actionTarget.contractId)}
        />
      )}
      {!!actionSubscription && (
        <>
          <StartDateUpdateModal
            isOpen={showStartDateModal}
            onCancel={() => setShowStartDateModal(false)}
            dialogTexts={{
              title: texts.confirmNewStartDate.title,
              content: format(texts.confirmNewStartDate.content, {
                userName: userDoc.name,
              }),
            }}
            currentStartDate={moment(actionTarget.clientView.serviceStartAt)}
            updateClientSubscription={updateClientSubscription}
          />
          <SubscriptionPauseModal
            isOpen={showConfirmPauseDialog}
            onCancel={() => setShowConfirmPauseDialog(false)}
            subscription={actionSubscription}
            userDoc={actionTarget.clientView}
            pauseClientSubscription={pauseClientSubscription}
          />
        </>
      )}
      <ConfirmDialog
        isOpen={showRetryPaymentDialog}
        onConfirm={retryCurrentInvoicePayment}
        onCancel={() => setShowRetryPaymentDialog(false)}
        dialogTexts={{
          title: texts.confirmRetry.title,
          content: format(texts.confirmRetry.content, {
            userName: userDoc.name,
            month: currentInvoice && moment.unix(currentInvoice.created).format(DateFormat.MONTH_NAME),
          }),
        }}
      />
      {!!showVoidInvoiceDialog && (
        <VoidInvoiceModal
          isOpen={showVoidInvoiceDialog}
          invoice={currentInvoice}
          onClose={() => setShowVoidInvoiceDialog(false)}
          onInvoiceVoided={onInvoiceVoided}
          stripeAccountId={currentInvoiceAccountId}
          contractId={currentInvoiceScope.contractId}
          productName={currentInvoiceScope.productName}
          coachName={pageCoachName}
        />
      )}
      {showRefundInvoiceDialog && (
        <RefundInvoiceModal
          isOpen={showRefundInvoiceDialog}
          invoice={currentInvoice}
          onClose={() => setShowRefundInvoiceDialog(false)}
          onInvoiceRefunded={onInvoiceRefunded}
          stripeAccountId={currentInvoiceAccountId}
          productName={currentInvoiceScope.productName}
          coachName={pageCoachName}
        />
      )}
    </Container>
  );
};

BillingSummary.propTypes = {
  userDoc: PropTypes.instanceOf(User).isRequired,
  billingTargets: PropTypes.arrayOf(PropTypes.object),
  isBlockedByFutureContract: PropTypes.func,
  scheduledAmendmentContractIds: PropTypes.arrayOf(PropTypes.string),
  onOpenContractModal: PropTypes.func,
};

BillingSummary.defaultProps = {
  billingTargets: [],
  isBlockedByFutureContract: () => false,
  scheduledAmendmentContractIds: [],
  onOpenContractModal: () => {},
};

export default compose(
  observer,
)(BillingSummary);
